import { Edge, Node } from '@xyflow/react';
import {
  ActionNode,
  ModelNode,
  PrimitiveActionParameter,
  TekstNode,
} from 'src/features/automations/[flowId]/components/utils/NodeDataTypes';
import { getActionConfig, getParameterById, intersectionOfLists } from 'src/features/flows/lib/general';
import { AppConfig } from 'src/features/apps/types/app.types';
import {
  ArrayNullableOperators,
  ArrayType,
  arrayTypeSchema,
  Condition,
  ConditionGroup,
  CustomType,
  customTypeSchema,
  Flow,
  Operators,
  PrimitiveNullableOperators,
  PrimitiveOperators,
  PrimitiveType,
  primitiveTypeSchema,
} from 'src/features/flows/types/flow.types';
import { FlowActionConfig, InputConfig, InputOption } from 'src/features/flows/types/flowAction.types';
import { Integration } from 'src/shared/types/integration.types';
import { ConditionGroupInput } from 'src/shared/constants/initializers.constant';

/**
 * Checks if a given type is a primitive type.
 * @param {string} type - The type to be checked.
 * @returns {boolean} - True if the type is a primitive type, false otherwise.
 */
export function isPrimitiveType(type: string): type is PrimitiveType {
  return primitiveTypeSchema.safeParse(type).success;
}

export function isCustomType(type: string): type is CustomType {
  return customTypeSchema.safeParse(type).success;
}

export function isArrayType(type: string): type is ArrayType {
  return arrayTypeSchema.safeParse(type).success;
}

export function isNullableOperator(type: string) {
  const nullableOperators: PrimitiveNullableOperators[] = ['EXISTS', 'DOES_NOT_EXIST'];
  const nullableArrayOperators: ArrayNullableOperators[] = ['IS_EMPTY', 'IS_NOT_EMPTY'];
  return [...nullableOperators, ...nullableArrayOperators].includes(type as PrimitiveNullableOperators);
}

/**
 * Checks if a given operator is a free-form operator. A free-form operator means that the right
 * operand can be anything even if there are options provided for the right operand.
 * @param {string} operator - The operator to be checked.
 * @returns {boolean} - True if the operator is free-form, false otherwise.
 */
export function isFreeFormOperator(operator: string): boolean {
  return !['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_IN', 'CONTAINS_ANY', 'NOT_CONTAINS_ANY'].includes(operator);
}

/**
 * Determines the type of operator.
 * @param {Operators} operator - The operator.
 * @returns {"ARRAY_TO_ARRAY" | "PRIMITIVE_TO_ARRAY" | "PRIMITIVE"} - The type of operator.
 */
export function operatorType(operator: Operators): 'ARRAY_TO_ARRAY' | 'PRIMITIVE_TO_ARRAY' | 'PRIMITIVE' {
  if (['CONTAINS_ANY', 'NOT_CONTAINS_ANY', 'IS_EMPTY', 'IS_NOT_EMPTY'].includes(operator)) {
    return 'ARRAY_TO_ARRAY';
  }
  if (['IN', 'NOT_IN'].includes(operator)) {
    return 'PRIMITIVE_TO_ARRAY';
  }
  return 'PRIMITIVE';
}

export function isPrimitiveOperator(operator: Operators): operator is PrimitiveOperators {
  return operatorType(operator) === 'PRIMITIVE';
}

/**
 * Gets the primitive type from a type string. string returns string, string[] returns string etc.
 * @param {string} type - The type string.
 * @returns {PrimitiveType} - The primitive type.
 */
export function getPrimitiveType(type: string): PrimitiveType {
  return type.split('[')[0] as PrimitiveType;
}

export function validateInput(values: string[], type?: string): boolean {
  if (!type) {
    return true;
  }

  for (let value of values) {
    let valid = true;
    switch (type) {
      case 'number':
        if (isNaN(Number(value))) {
          valid = false;
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          valid = false;
        }
        break;
    }
    if (!valid) {
      return false;
    }
  }
  return true;
}

export type FlowIssue = {
  nodeId: string | null;
  message: string;
};

// action param values is an array: if primitive action type only one element in the array
// condition not complete, check empty string, empty array
// condition rightOperand is an array: if primitive operator only one element in the array
// action Id and action Param Id should be the same as in the possible actions array
// email is email, number is number, boolean is "true" or "false"
// condition operator matches the type of the operands
// condition rightOperand type matches the type of the operator
// app node required variable or secret not set
export function validateFlow(
  nodes: TekstNode[],
  edges: Edge[],
  possibleActions: FlowActionConfig[],
  flow: Flow,
  integrationMap: Map<string, Integration>,
  tekstApps: AppConfig[]
) {
  const issues: FlowIssue[] = [];
  let actionNodeCount = 0;

  if (flow?.integrations.length) {
    for (const integrationId of flow.integrations) {
      if (!integrationMap.has(integrationId)) {
        issues.push({ nodeId: null, message: `Trigger inbox with ID ${integrationId} not found` });
      }
    }
  }

  for (const node of nodes) {
    if (node.type === 'newNode') {
      issues.push({ nodeId: node.id, message: 'Empty node' });
    }
    // check dangling nodes
    const hasParent = edges.find((edge) => edge.target === node.id);
    if (!hasParent && node.data.type !== 'trigger') {
      const typeDisplay = node.data.type === 'utility' ? 'action' : node.data.type;
      issues.push({ nodeId: node.id, message: `Dangling ${typeDisplay || 'node'}: ${node.data.name || 'New Node'}` });
    }
    // check condition nodes
    // filled in, types match
    const nodeData = node.data;
    if (nodeData.type === 'condition') {
      if (nodeData.hydrationError) {
        if (nodeData.hydrationError.severity === 'warning') {
          issues.push({ nodeId: node.id, message: `Issues found in ${nodeData.name}` });
        } else {
          issues.push({ nodeId: node.id, message: `Invalid ${nodeData.name} step.` });
        }
      }
      if (nodeData.conditionGroup.conditions.length === 0) {
        issues.push({ nodeId: node.id, message: `Empty condition in ${nodeData.name}` });
      }
      for (const condition of nodeData.conditionGroup.conditions) {
        if (condition.isGroup === false) {
          const completed =
            condition.leftOperand?.length &&
            condition.type?.length &&
            condition.operator?.length &&
            (isNullableOperator(condition.operator) || condition.rightOperand?.length);
          if (!completed) {
            issues.push({ nodeId: node.id, message: 'Incomplete condition' });
          }
        }
      }
    }

    if (nodeData.type === 'approval') {
      if (nodeData.hydrationError) {
        if (nodeData.hydrationError.severity === 'warning') {
          issues.push({ nodeId: node.id, message: `Issues found in ${nodeData.name}` });
        } else {
          issues.push({ nodeId: node.id, message: `Invalid ${nodeData.name} step.` });
        }
      }

      if (nodeData.valuesToApprove.length === 0) {
        issues.push({ nodeId: node.id, message: `No values to approve in ${nodeData.name}` });
      }

      for (const valueToApprove of nodeData.valuesToApprove) {
        if (!valueToApprove.value) {
          issues.push({ nodeId: node.id, message: 'Incomplete variable to approve' });
        }
      }
    }

    // check action nodes
    if (nodeData.type === 'action' || nodeData.type === 'utility') {
      if (nodeData.hydrationError) {
        if (nodeData.hydrationError.severity === 'warning') {
          issues.push({ nodeId: node.id, message: `Issues found in ${nodeData.name}` });
        } else {
          issues.push({ nodeId: node.id, message: `Invalid ${nodeData.name} action.` });
        }
      }
      actionNodeCount++;
      const relatedActionConfig = getActionConfig(possibleActions, nodeData.id, nodeData.integrationType);
      if (!relatedActionConfig) {
        issues.push({
          nodeId: node.id,
          message: `Unknown action: ${nodeData.name}. This action might no longer be available.`,
        });
      } else {
        const actionIssues = validateAction(node as Node<ActionNode>, relatedActionConfig, flow, integrationMap);
        issues.push(...actionIssues);
      }
    }
    // check app nodes
    if (nodeData.type === 'app') {
      if (nodeData.hydrationError) {
        if (nodeData.hydrationError.severity === 'warning') {
          issues.push({ nodeId: node.id, message: `Issues found in ${nodeData.name}` });
        } else {
          issues.push({ nodeId: node.id, message: `Invalid app: ${nodeData.name}.` });
        }
      }
      actionNodeCount++;
      const appConfig = tekstApps.find((app) => app.id === nodeData.id);
      let versionVariableConfig = appConfig?.variables;

      if (nodeData.version) {
        const versionConfig = appConfig?.AppVersionHistory.find((version) => version.version === nodeData.version);
        versionVariableConfig = versionConfig?.variables;
      }

      const requiredVariables = versionVariableConfig?.filter((variable) => !variable.optional);
      if (requiredVariables && requiredVariables?.length > 0) {
        for (const variable of requiredVariables) {
          const nodeVariable = getParameterById(nodeData.variables, variable.id);
          if (!nodeVariable || !nodeVariable.values.length) {
            issues.push({
              nodeId: node.id,
              message: `Missing Variable in ${nodeData.name}: ${variable.name}`,
            });
          }
        }
      }
      for (const secret in nodeData.secrets) {
        if (nodeData.secrets[secret].required && !nodeData.secrets[secret].value) {
          issues.push({ nodeId: node.id, message: `Missing Secret in ${nodeData.name}` });
        }
      }
    }

    // check model nodes
    if (nodeData.type === 'model') {
      const modelNode = nodeData as ModelNode;
      if (modelNode.modelType === 'extraction') {
        if (!modelNode.modelId) {
          issues.push({ nodeId: node.id, message: `No model selected in ${nodeData.name}` });
        }
      } else if (modelNode.modelType === 'classification') {
        if (!modelNode.modelIds || modelNode.modelIds.length === 0) {
          issues.push({ nodeId: node.id, message: `No models selected in ${nodeData.name}` });
        }
      }
    }
  }

  if (actionNodeCount === 0) {
    issues.push({ nodeId: null, message: 'Automation must contain at least one action or app.' });
  }

  return issues;
}

export function validateAction(
  node: Node<ActionNode>,
  relatedActionConfig: FlowActionConfig,
  flow: Flow,
  integrationMap: Map<string, Integration>
): FlowIssue[] {
  const issues: FlowIssue[] = [];
  const nodeData = node.data;

  if (relatedActionConfig.integrationSpecificInputs) {
    const selectedIntegrationId = nodeData.integrationId;
    if (!selectedIntegrationId) {
      issues.push({
        nodeId: node.id,
        message: `No integration selected for ${nodeData.name} action`,
      });
    }
    const integrationSpecificInputs = selectedIntegrationId
      ? relatedActionConfig.integrationSpecificInputs[selectedIntegrationId]
      : undefined;
    if (!integrationSpecificInputs) {
      issues.push({
        nodeId: node.id,
        message: `Invalid integration selected for ${nodeData.name} action`,
      });
    } else {
      for (const actionInput of integrationSpecificInputs) {
        recursivelyValidateAction(actionInput, issues);
      }
    }
  } else {
    const requiredInputs = relatedActionConfig.inputs?.filter((input) => !input.optional) || [];
    for (const actionInput of requiredInputs) {
      recursivelyValidateAction(actionInput, issues);
    }
  }

  function recursivelyValidateAction(actionInputConfig: InputConfig, issues: FlowIssue[]): FlowIssue[] {
    const nodeData = node.data;

    if (actionInputConfig.optional) {
      return issues;
    }

    //  recursive validation of custom[] inputs
    if (!actionInputConfig.optional && actionInputConfig.type === 'custom[]') {
      // If there are zero instances of this custom[] parameter, skip nested validation
      const hasAnyCustomInstances = node.data.actionParameters?.some(
        (param: any) => param.id === actionInputConfig.id && param.type === 'custom[]'
      );
      if (!hasAnyCustomInstances) {
        return issues;
      }

      for (const actionInput of actionInputConfig.items) {
        recursivelyValidateAction(actionInput, issues);
      }
    }
    // check if the input is present in the action parameters
    else {
      const nodeActionParam = getParameterById(
        nodeData.actionParameters,
        actionInputConfig.id
      ) as PrimitiveActionParameter;

      const overrides = Object.keys(nodeActionParam?.overrides || {});

      if (!nodeActionParam) {
        issues.push({
          nodeId: node.id,
          message: `"${actionInputConfig.name}" missing in ${nodeData.name} action`,
        });
        return issues;
      }

      const hasValues = nodeActionParam.values.length > 0 || overrides.length > 0;

      // empty values for disable default params are caught in the override check
      if (actionInputConfig.type !== 'boolean' && !hasValues && !actionInputConfig.disableDefault) {
        issues.push({
          nodeId: node.id,
          message: `Empty "${actionInputConfig.name}" in ${nodeData.name} action`,
        });
      } else {
        // check if select input options exist in the config
        if (actionInputConfig.hasOptions && actionInputConfig.options) {
          // in the default parameter values, only default options/intersecting options are allowed
          const selectedIntegrationOptions = Object.entries(actionInputConfig.options).reduce(
            (acc, [integrationId, options]) => {
              if (flow?.integrations.includes(integrationId)) {
                acc.push(options);
              }
              return acc;
            },
            [] as InputOption[][]
          );
          const intersection = intersectionOfLists(...selectedIntegrationOptions);
          const availableOptionSet = new Set(
            [...intersection, ...(actionInputConfig.options?.DEFAULT || [])].map((option) => option.id)
          );

          nodeActionParam.values.forEach((value) => {
            if (!availableOptionSet.has(value.id)) {
              issues.push({
                nodeId: node.id,
                message: `Invalid option selected for "${actionInputConfig.name}" in ${nodeData.name} action`,
              });
            }
          });
        }
      }

      if (actionInputConfig.disableDefault && overrides.length !== flow?.integrations.length) {
        issues.push({
          nodeId: node.id,
          message: `Missing "${actionInputConfig.name}" for ${flow?.integrations.length - overrides.length} integrations in ${nodeData.name} action `,
        });
      }

      // check if the override options exist in the integration config
      if (
        overrides.length > 0 &&
        actionInputConfig.hasOptions &&
        actionInputConfig.options &&
        nodeActionParam.overrides
      ) {
        Object.entries(nodeActionParam.overrides).forEach(([integrationId, values]) => {
          // in the default parameter values, only default options/intersecting options are allowed
          const selectedIntegrationOptions = actionInputConfig.options?.[integrationId] || [];
          const availableOptionSet = new Set(
            [...selectedIntegrationOptions, ...(actionInputConfig.options?.DEFAULT || [])].map((option) => option.id)
          );

          values.forEach((value) => {
            if (!availableOptionSet.has(value.id)) {
              issues.push({
                nodeId: node.id,
                message: `Invalid override option selected for "${actionInputConfig.name}" in ${nodeData.name} action for ${integrationMap.get(integrationId)?.name}`,
              });
            }
          });
        });
      }
    }

    return issues;
  }

  return issues;
}

export function validateCondition(
  conditionGroup: ConditionGroup | ConditionGroupInput,
  issues: Partial<FlowIssue>[] = []
): Partial<FlowIssue>[] {
  if (!conditionGroup.isGroup) {
    issues.push({ message: 'Condition group is not a group' });
    return issues;
  }

  if (conditionGroup.conditions.length === 0) {
    issues.push({ message: 'Empty conditions in condition group' });
    return issues;
  }

  for (const condition of conditionGroup.conditions) {
    if (condition.isGroup) {
      return validateCondition(condition, issues);
    }
    // cast as primitive condition because typescript is not smart enough to infer the type
    const primitiveCondition = condition as Condition;
    if (!primitiveCondition.leftOperand?.length) {
      issues.push({ message: 'Left operand is empty' });
    }
    if (!primitiveCondition.dataType?.length) {
      issues.push({ message: 'Type is empty' });
    }
    if (!primitiveCondition.operator?.length) {
      issues.push({ message: 'Operator is empty' });
    }
    if (isNullableOperator(primitiveCondition.operator)) {
      if (primitiveCondition.rightOperand?.length !== 0) {
        issues.push({ message: 'Right operand is not empty for nullable operator' });
      }
    } else {
      if (!primitiveCondition.rightOperand?.length) {
        issues.push({ message: 'Right operand is empty' });
      }
    }
  }
  return issues;
}
